import { clearAnalyticsCookies } from '../consent/cookies';

/** Public: it ships in the page anyway. */
export const GA4_MEASUREMENT_ID = 'G-X1HYPVGSFH';

type Gtag = (...args: unknown[]) => void;

declare global {
    interface Window {
        dataLayer?: unknown[];
        gtag?: Gtag;
    }
}

let booted = false;

/** Google's documented switch: while set, gtag.js sends nothing for this property. */
const setGaDisabled = (measurementId: string, disabled: boolean): void => {
    (window as unknown as Record<string, unknown>)[`ga-disable-${measurementId}`] = disabled;
};

/**
 * Starts Google Analytics. Nothing is requested from Google before this runs, so it is only called
 * once the visitor has said yes. Calling it again (consent given, withdrawn, given) just flips it back on.
 */
export const enableGa4 = (measurementId: string = GA4_MEASUREMENT_ID): void => {
    setGaDisabled(measurementId, false);

    if (booted) {
        window.gtag?.('consent', 'update', { analytics_storage: 'granted' });
        return;
    }
    booted = true;

    window.dataLayer = window.dataLayer ?? [];
    // gtag.js only recognises commands pushed as the native `arguments` object. A rest-param array is
    // silently queued and never sent, so this has to be the classic function.
    const gtag: Gtag = function () {
        // eslint-disable-next-line prefer-rest-params -- gtag.js needs the native arguments object
        window.dataLayer!.push(arguments);
    };
    window.gtag = gtag;

    gtag('js', new Date());
    // Page views are sent by hand (see ./index.ts), so GA mustn't send its own on load.
    gtag('config', measurementId, { send_page_view: false });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    document.head.appendChild(script);
};

/** Stops sending, tells Google consent is withdrawn, and removes the cookies it already set. */
export const disableGa4 = (measurementId: string = GA4_MEASUREMENT_ID): void => {
    setGaDisabled(measurementId, true);

    if (booted) window.gtag?.('consent', 'update', { analytics_storage: 'denied' });

    clearAnalyticsCookies();
};
